/**
 * Pickups (workstream C) — a pickup is its own object: one trip covering N
 * projects' orders. These hooks drive the supervisor Pickups page + detail and
 * the crew-assignment picker. Crew assignment is WARN-not-block (D-11): the
 * set/reassign responses carry a `warnings` array on SUCCESS (overlaps to eyeball),
 * never a thrown DOUBLE_BOOKED.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PickupAssignments,
  PickupAssignmentsSetInput,
  PickupCreateInput,
  PickupDetail,
  PickupList,
  PickupReassignInput,
  PickupTransitionInput,
  PickupUpdateInput,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';

const KEY = ['pickups'] as const;

export function pickupsListOptions() {
  return queryOptions({
    queryKey: [...KEY, 'list'],
    queryFn: () => apiCall<PickupList>('/pickups'),
    staleTime: 10_000,
  });
}
export function usePickups() {
  return useQuery(pickupsListOptions());
}

export function usePickup(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    enabled: !!id,
    queryFn: () => apiCall<PickupDetail>(`/pickups/${id}`),
  });
}

export function useCreatePickup() {
  const qc = useQueryClient();
  return useMutation<PickupDetail, Error, PickupCreateInput>({
    mutationFn: (body) => apiCall<PickupDetail>('/pickups', { method: 'POST', body }),
    onSuccess: (d) => {
      qc.setQueryData([...KEY, 'detail', d.id], d);
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

export function useUpdatePickup(id: string) {
  const qc = useQueryClient();
  return useMutation<PickupDetail, Error, PickupUpdateInput>({
    mutationFn: (body) => apiCall<PickupDetail>(`/pickups/${id}`, { method: 'PATCH', body }),
    onSuccess: (d) => {
      qc.setQueryData([...KEY, 'detail', id], d);
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['crew-today'] });
    },
  });
}

export function useDeletePickup(id: string) {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, void>({
    mutationFn: () => apiCall<{ id: string }>(`/pickups/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['crew-today'] });
    },
  });
}

/** Crew Start / Finish (C3). */
export function useTransitionPickup(id: string) {
  const qc = useQueryClient();
  return useMutation<PickupDetail, Error, PickupTransitionInput>({
    mutationFn: (body) => apiCall<PickupDetail>(`/pickups/${id}/transition`, { method: 'POST', body }),
    onSuccess: (d) => {
      qc.setQueryData([...KEY, 'detail', id], d);
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['crew-today'] });
      qc.invalidateQueries({ queryKey: ['crew-schedule'] });
    },
  });
}

export const pickupAssignmentsKey = (id: string) => ['pickup-assignments', id] as const;

export function usePickupAssignments(id: string | undefined) {
  return useQuery({
    queryKey: pickupAssignmentsKey(id ?? ''),
    enabled: !!id,
    queryFn: () => apiCall<PickupAssignments>(`/pickups/${id}/assignments`),
  });
}

/** Invalidate every surface a crew change on a pickup touches. */
function invalidatePickupCrew(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: pickupAssignmentsKey(id) });
  qc.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
  qc.invalidateQueries({ queryKey: KEY });
  qc.invalidateQueries({ queryKey: ['dispatch'] });
  qc.invalidateQueries({ queryKey: ['crew-today'] });
}

export function useSetPickupAssignments(id: string) {
  const qc = useQueryClient();
  return useMutation<PickupAssignments, Error, PickupAssignmentsSetInput>({
    mutationFn: (body) => apiCall<PickupAssignments>(`/pickups/${id}/assignments`, { method: 'PUT', body }),
    onSuccess: (d) => {
      qc.setQueryData(pickupAssignmentsKey(id), d);
      invalidatePickupCrew(qc, id);
    },
  });
}

export function useReassignPickup(id: string) {
  const qc = useQueryClient();
  return useMutation<PickupAssignments, Error, PickupReassignInput>({
    mutationFn: (body) => apiCall<PickupAssignments>(`/pickups/${id}/reassign`, { method: 'POST', body }),
    onSuccess: (d) => {
      qc.setQueryData(pickupAssignmentsKey(id), d);
      invalidatePickupCrew(qc, id);
    },
  });
}
